/* eslint-disable */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from './i18n';
import { getRegionName } from './deviceNameUtils';

// ─── constants & helpers ──────────────────────────────────────────────────────

// BUCKET_DEF is a function so t() works at call time, not module init
function getBucketDef(t) {
  return [
    { key: 'inactive', label: t('deviceActivity.bucketInactive'), color: '#A32D2D', bg: '#FCEBEB', dot: '#E24B4A' },
    { key: 'never',    label: t('deviceActivity.bucketNever'),    color: '#5F5E5A', bg: '#F1EFE8', dot: '#888780' },
    { key: 'overdue',  label: t('deviceActivity.bucketOverdue'),  color: '#854F0B', bg: '#FAEEDA', dot: '#EF9F27' },
    { key: 'recent',   label: t('deviceActivity.bucketRecent'),   color: '#3B6D11', bg: '#EAF3DE', dot: '#97C459' },
    { key: 'today',    label: t('deviceActivity.bucketToday'),    color: '#3B6D11', bg: '#EAF3DE', dot: '#639922' },
  ];
}

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  return (Date.now() - new Date(dateStr).getTime()) / 86400000;
}

function getBucket(device, windowDays) {
  const d = daysSince(device.lastSyncDateTime);
  if (d === Infinity) return 'never';
  if (d <= 1)         return 'today';
  if (d <= windowDays) return 'recent';
  if (d <= 30)        return 'overdue';
  return 'inactive';
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const days = daysSince(dateStr);
  if (days < 1)  return `${Math.round(days * 24)}h ago`;
  if (days < 7)  return `${Math.round(days)}d ago`;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function pct(n, total) { return total === 0 ? 0 : Math.round((n / total) * 100); }

function ragStatus(rate) {
  if (rate >= 90) return 'good';
  if (rate >= 75) return 'warn';
  return 'crit';
}

const RAG_STYLES = {
  good: { background: '#EAF3DE', color: '#3B6D11' },
  warn: { background: '#FAEEDA', color: '#854F0B' },
  crit: { background: '#FCEBEB', color: '#A32D2D' },
};
// RAG_LABEL is a function so t() is called inside components, not at module level
function getRagLabel(t) {
  return { good: t('deviceActivity.statusOnTrack'), warn: t('deviceActivity.statusAttention'), crit: t('deviceActivity.statusCritical') };
}
const NUM_COLOR  = (r) => r >= 90 ? '#3B6D11' : r >= 75 ? '#854F0B' : '#A32D2D';
const BAR_COLOR  = (r) => r >= 90 ? '#639922'  : r >= 75 ? '#EF9F27'  : '#D85A30';

// BUCKET_ORDER for sort: worst first
const BUCKET_SORT = { inactive: 0, never: 1, overdue: 2, recent: 3, today: 4 };

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportCSV(devices, title, windowDays, t) {
  const rows = [[t('deviceActivity.deviceCol'), 'User', t('deviceActivity.schoolCol'), t('intune.region'), t('intune.district'), 'Type', t('deviceActivity.lastSyncCol'), t('deviceActivity.statusCol')]];
  devices.forEach(d => {
    rows.push([
      d.deviceName || '',
      d.userDisplayName || d.primaryUserUPN || '',
      d.school || '',
      d.region || '',
      d.district || '',
      d.deviceOwnerType || '',
      d.lastSyncDateTime ? new Date(d.lastSyncDateTime).toLocaleDateString('en-GB') : t('deviceActivity.bucketNever'),
      getBucket(d, windowDays),
    ]);
  });
  const csv  = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${title.replace(/[^a-z0-9]/gi,'_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── DrillDownView ────────────────────────────────────────────────────────────
// Full-page replacement when a KPI card is clicked

function DrillDownView({ panel, windowDays, onBack }) {
  const { t } = useTranslation();
  const BUCKET_DEF = getBucketDef(t);
  const RAG_LABEL   = getRagLabel(t);
  const [search,      setSearch]      = useState('');
  const [sortCol,     setSortCol]     = useState('status');
  const [sortAsc,     setSortAsc]     = useState(true);
  const [activeBuckets, setActiveBuckets] = useState(() => {
    // Pre-select the buckets that were clicked — anything "bad" by default
    if (panel.preselect) return new Set(panel.preselect);
    return new Set(['inactive', 'never', 'overdue', 'recent', 'today']);
  });

  const toggleBucket = (k) => {
    setActiveBuckets(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  // Counts per bucket for pill badges
  const bucketCounts = useMemo(() => {
    const m = {};
    panel.devices.forEach(d => {
      const b = getBucket(d, windowDays);
      m[b] = (m[b] || 0) + 1;
    });
    return m;
  }, [panel.devices, windowDays]);

  // Filter + sort
  const visible = useMemo(() => {
    let list = panel.devices.filter(d => {
      if (!activeBuckets.has(getBucket(d, windowDays))) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (d.deviceName||'').toLowerCase().includes(q) ||
             (d.userDisplayName||'').toLowerCase().includes(q) ||
             (d.primaryUserUPN||'').toLowerCase().includes(q) ||
             (d.school||'').toLowerCase().includes(q) ||
             (d.region||'').toLowerCase().includes(q);
    });

    list = [...list].sort((a, b) => {
      let va, vb;
      if      (sortCol === 'status')  { va = BUCKET_SORT[getBucket(a,windowDays)]; vb = BUCKET_SORT[getBucket(b,windowDays)]; }
      else if (sortCol === 'device')  { va = a.deviceName||''; vb = b.deviceName||''; }
      else if (sortCol === 'user')    { va = a.userDisplayName||''; vb = b.userDisplayName||''; }
      else if (sortCol === 'school')  { va = a.region||''; vb = b.region||''; }
      else if (sortCol === 'sync')    { va = a.lastSyncDateTime||''; vb = b.lastSyncDateTime||''; }
      if (va === vb) return 0;
      if (typeof va === 'string') return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortAsc ? va - vb : vb - va;
    });
    return list;
  }, [panel.devices, activeBuckets, search, sortCol, sortAsc, windowDays]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(true); }
  };

  const SortTh = ({ col, label, style: sx }) => (
    <th
      onClick={() => toggleSort(col)}
      style={{ fontSize: 11, fontWeight: 500, color: sortCol === col ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', padding: '0 12px 8px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', ...sx }}
    >
      {label}{sortCol === col ? (sortAsc ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const card = { background:'var(--color-background-primary)', border:'0.5px solid var(--color-border-tertiary)', borderRadius:12, overflow:'hidden' };
  const divider = '0.5px solid var(--color-border-tertiary)';

  return (
    <div style={{ paddingBottom: 40 }}>

      {/* Back + breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button
          onClick={onBack}
          style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:14, fontWeight:700, color:'#185FA5', background:'#E6F1FB', border:'1.5px solid #B5D4F4', borderRadius:8, padding:'9px 20px', cursor:'pointer' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6L8 10" stroke="#185FA5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Back to Activity
        </button>
        <div style={{ fontSize:13, color:'var(--color-text-secondary)' }}>
          Device Activity &rsaquo; <span style={{ color:'var(--color-text-primary)', fontWeight:500 }}>{panel.title}</span>
        </div>
        <button
          onClick={() => exportCSV(visible, panel.title, windowDays, t)}
          style={{ marginLeft:'auto', fontSize:12, color:'#185FA5', fontWeight:500, background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v7M3 5l3 3 3-3M2 10h8" stroke="#185FA5" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          export CSV
        </button>
      </div>

      <div style={card}>

        {/* Bucket filter pills */}
        <div style={{ display:'flex', gap:6, padding:'10px 16px', borderBottom:divider, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:11, color:'var(--color-text-secondary)', marginRight:2 }}>show:</span>
          {BUCKET_DEF.map(b => {
            const count = bucketCounts[b.key] || 0;
            const on    = activeBuckets.has(b.key);
            return (
              <button
                key={b.key}
                onClick={() => toggleBucket(b.key)}
                style={{
                  display:'inline-flex', alignItems:'center', gap:5,
                  padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:500, cursor:'pointer',
                  background: on ? b.bg : 'transparent',
                  border: on ? `0.5px solid ${b.dot}` : '0.5px solid var(--color-border-secondary)',
                  color: on ? b.color : 'var(--color-text-secondary)',
                }}
              >
                <span style={{ width:6, height:6, borderRadius:'50%', background: on ? b.dot : 'var(--color-border-secondary)', display:'inline-block' }} />
                {b.label}
                {count > 0 && <span style={{ background: on ? b.dot : 'var(--color-background-secondary)', color: on ? '#fff' : 'var(--color-text-secondary)', borderRadius:10, padding:'0 5px', fontSize:10, lineHeight:'16px' }}>{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ padding:'10px 16px', borderBottom:divider }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('deviceActivity.filterPlaceholder')}
            style={{ width:'100%', padding:'7px 12px', fontSize:13, borderRadius:8, border:'0.5px solid var(--color-border-secondary)', background:'var(--color-background-primary)', color:'var(--color-text-primary)', boxSizing:'border-box' }}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:580 }}>
            <thead>
              <tr style={{ borderBottom:divider, background:'var(--color-background-secondary)' }}>
                <SortTh col="device" label={t("deviceActivity.deviceCol")} style={{ paddingLeft:16 }} />
                <SortTh col="user"   label={t("deviceActivity.userCol")} />
                <SortTh col="school" label={t("deviceActivity.regionSchoolCol")} />
                <SortTh col="sync"   label={t("deviceActivity.lastSyncCol")} style={{ textAlign:'right' }} />
                <SortTh col="status" label={t("deviceActivity.statusCol")} style={{ textAlign:'right', paddingRight:16 }} />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={5} style={{ padding:'40px 16px', textAlign:'center', color:'var(--color-text-secondary)', fontSize:13 }}>{t("deviceActivity.noDevicesMatch")}</td></tr>
              ) : visible.map(d => {
                const b      = getBucket(d, windowDays);
                const bDef   = BUCKET_DEF.find(x => x.key === b) || BUCKET_DEF[0];
                const isT    = d.deviceOwnerType === t('deviceActivity.teachers');
                return (
                  <tr
                    key={d.id || d.deviceName}
                    style={{ borderBottom:divider }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding:'10px 12px 10px 16px', verticalAlign:'top' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:7 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:bDef.dot, flexShrink:0, marginTop:3 }} />
                        <div>
                          <div style={{ fontWeight:500 }}>{d.deviceName || '—'}</div>
                          <div style={{ fontSize:11, marginTop:2 }}>
                            <span style={{ background: isT ? '#E6F1FB' : '#EAF3DE', color: isT ? '#185FA5' : '#3B6D11', padding:'0 5px', borderRadius:4, fontSize:10, fontWeight:500 }}>
                              {isT ? 'T' : 'S'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'10px 12px', verticalAlign:'top', color:'var(--color-text-secondary)' }}>
                      <div>{d.userDisplayName || '—'}</div>
                      {d.primaryUserUPN && d.primaryUserUPN !== d.userDisplayName && (
                        <div style={{ fontSize:11, color:'var(--color-text-secondary)', marginTop:1 }}>{d.primaryUserUPN}</div>
                      )}
                    </td>
                    <td style={{ padding:'10px 12px', verticalAlign:'top', color:'var(--color-text-secondary)' }}>
                      <div>{d.region || '—'}</div>
                      <div style={{ fontSize:11, marginTop:1 }}>{d.school || '—'} · {d.district || ''}</div>
                    </td>
                    <td style={{ padding:'10px 12px', verticalAlign:'top', textAlign:'right', color:'var(--color-text-secondary)', whiteSpace:'nowrap' }}>
                      {fmtDate(d.lastSyncDateTime)}
                    </td>
                    <td style={{ padding:'10px 16px 10px 12px', verticalAlign:'top', textAlign:'right', whiteSpace:'nowrap' }}>
                      <span style={{ ...RAG_STYLES[ragStatus(b === 'today' || b === 'recent' ? 100 : b === 'overdue' ? 80 : 0)], padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:500, background:bDef.bg, color:bDef.color }}>
                        {bDef.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary footer */}
        <div style={{ padding:'8px 16px', background:'var(--color-background-secondary)', borderTop:divider, display:'flex', gap:16, fontSize:11, flexWrap:'wrap' }}>
          <span style={{ color:'var(--color-text-secondary)' }}>
            showing <strong style={{ color:'var(--color-text-primary)' }}>{visible.length}</strong> {t('deviceActivity.of')} {panel.devices.length} {t('deviceActivity.devices')}
          </span>
          {BUCKET_DEF.filter(b => bucketCounts[b.key]).map(b => (
            <span key={b.key} style={{ color:b.color, fontWeight:500 }}>{bucketCounts[b.key]} {b.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────


// ─── Timeline Card ────────────────────────────────────────────────────────────

// Mini date picker for chart range
function ChartDateInput({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? parseInt(value.split('-')[0]) : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? parseInt(value.split('-')[1]) - 1 : new Date().getMonth());
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const display = value ? value.split('-').reverse().join('/') : 'DD/MM/YYYY';
  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDay = (y, m) => { let d = new Date(y, m, 1).getDay() - 1; return d < 0 ? 6 : d; };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Mo','Tu','We','Th','Fr','Sa','Su'];
  const cells = [...Array(firstDay(viewYear, viewMonth)).fill(null), ...Array(daysInMonth(viewYear, viewMonth)).fill(0).map((_,i)=>i+1)];
  const selectedDay = value && parseInt(value.split('-')[0])===viewYear && parseInt(value.split('-')[1])-1===viewMonth ? parseInt(value.split('-')[2]) : null;
  const pick = (d) => { onChange(`${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`); setOpen(false); };
  const prev = () => viewMonth===0 ? (setViewMonth(11),setViewYear(y=>y-1)) : setViewMonth(m=>m-1);
  const next = () => viewMonth===11 ? (setViewMonth(0),setViewYear(y=>y+1)) : setViewMonth(m=>m+1);

  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ padding:'3px 7px', border:'1px solid #e0e0e0', borderRadius:4, fontSize:11, cursor:'pointer', background:'white', userSelect:'none', minWidth:82, color: value ? '#333' : '#aaa' }}>
        {display} 📅
      </div>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, marginTop:2, zIndex:1000, background:'white', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,0.15)', padding:10, width:210 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <button onClick={prev} style={{ border:'none', background:'none', cursor:'pointer', fontSize:14 }}>‹</button>
            <span style={{ fontWeight:600, fontSize:12 }}>{MONTHS[viewMonth]} {viewYear}</span>
            <button onClick={next} style={{ border:'none', background:'none', cursor:'pointer', fontSize:14 }}>›</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1, marginBottom:3 }}>
            {DAYS.map(d=><div key={d} style={{ textAlign:'center', fontSize:9, fontWeight:700, color:'#94a3b8' }}>{d}</div>)}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:1 }}>
            {cells.map((day,i)=>(
              <div key={i} onMouseDown={(e)=>{e.preventDefault();e.stopPropagation();if(day)pick(day);}} style={{ textAlign:'center', padding:'4px 1px', fontSize:11, borderRadius:3, cursor:day?'pointer':'default', background:day===selectedDay?'#0078d4':'transparent', color:day===selectedDay?'white':day?'#333':'transparent' }}
                onMouseEnter={e=>{if(day&&day!==selectedDay)e.currentTarget.style.background='#f0f7ff';}}
                onMouseLeave={e=>{if(day!==selectedDay)e.currentTarget.style.background='transparent';}}>
                {day||''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineCard({ devices, label, color, dateRange, snapshotTimeline = [], ownerType }) {
  const now = new Date();

  const defaultStart = new Date(now); defaultStart.setDate(defaultStart.getDate() - 29);
  const [chartStart, setChartStart] = useState(
    dateRange?.startDate
      ? dateRange.startDate.split('T')[0]
      : defaultStart.toISOString().split('T')[0]
  );
  const [chartEnd, setChartEnd] = useState(
    dateRange?.endDate
      ? dateRange.endDate.split('T')[0]
      : now.toISOString().split('T')[0]
  );

  useEffect(() => {
    if (dateRange?.startDate) setChartStart(dateRange.startDate.split('T')[0]);
    if (dateRange?.endDate)   setChartEnd(dateRange.endDate.split('T')[0]);
  }, [dateRange?.startDate, dateRange?.endDate]);

  // Use snapshot data (real daily counts from DB) when available
  const hasSnapshots = snapshotTimeline && snapshotTimeline.length > 0;

  const chartData = useMemo(() => {
    const start = new Date(chartStart + 'T00:00:00');
    const end   = new Date(chartEnd   + 'T23:59:59');
    if (isNaN(start) || isNaN(end) || start > end) return [];

    if (hasSnapshots) {
      // Use real snapshot data — filter to date range and pick the right owner type count
      // Deduplicate by date (keep latest collected_at per day), then filter range
      const byDate = {};
      snapshotTimeline.forEach(row => {
        const dateStr = String(row.snapshot_date).split('T')[0];
        if (!byDate[dateStr] || row.collected_at > byDate[dateStr].collected_at) {
          byDate[dateStr] = row;
        }
      });
      return Object.entries(byDate)
        .filter(([dateStr]) => dateStr >= chartStart && dateStr <= chartEnd)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateStr, row]) => ({
          date: dateStr,
          label: new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', { day:'2-digit', month:'short' }),
          count: ownerType === 'Teacher'
            ? parseInt(row.teacher_synced_24h || 0)
            : parseInt(row.student_synced_24h || 0),
          total: ownerType === 'Teacher'
            ? parseInt(row.teacher_devices || 0)
            : parseInt(row.student_devices || 0),
        }));
    }

    // Fallback: count by lastSyncDateTime per day (current data only)
    const counts = {};
    const cursor = new Date(start);
    while (cursor <= end) {
      counts[cursor.toISOString().split('T')[0]] = 0;
      cursor.setDate(cursor.getDate() + 1);
    }
    devices.forEach(d => {
      if (!d.lastSyncDateTime) return;
      const day = new Date(d.lastSyncDateTime).toISOString().split('T')[0];
      if (day in counts) counts[day]++;
    });
    return Object.entries(counts).map(([date, count]) => ({
      date,
      label: new Date(date + 'T12:00:00').toLocaleDateString('en-GB', { day:'2-digit', month:'short' }),
      count,
    }));
  }, [devices, chartStart, chartEnd, snapshotTimeline, hasSnapshots, ownerType]);

  const inputStyle = {
    fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 5,
    padding: '3px 6px', outline: 'none', cursor: 'pointer',
    background: 'white', color: '#374151', width: '100%', boxSizing: 'border-box',
  };

  // Fixed bar width so bars stay tight regardless of data count
  const BAR_W = 18;
  const BAR_GAP = 4;
  const chartWidth = Math.max(chartData.length * (BAR_W + BAR_GAP) + 50, 80);

  const dateInputStyle = {
    fontSize: 10, border: '1px solid #e2e8f0', borderRadius: 4,
    padding: '2px 4px', outline: 'none', cursor: 'pointer',
    background: 'white', color: '#374151', colorScheme: 'light',
  };

  return (
    <div style={{
      background: 'white', borderRadius: 12, padding: '14px 16px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '0.5px solid var(--color-border-tertiary)',
      display: 'flex', flexDirection: 'column', gap: 8,
      overflow: 'hidden',
    }}>
      {/* Header row: title left, date pickers right */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0 }}>
          <span style={{ fontSize:14 }}>📈</span>
          <div>
            <div style={{ fontSize:10, color:'var(--color-text-secondary)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', whiteSpace:'nowrap' }}>Daily Syncs</div>
            <div style={{ fontSize:8, color:'#94a3b8' }}>{ownerType} — synced per day at midnight snapshot</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
          <ChartDateInput value={chartStart} onChange={setChartStart} />
          <span style={{ fontSize:9, color:'#94a3b8' }}>–</span>
          <ChartDateInput value={chartEnd} onChange={setChartEnd} />
        </div>
      </div>

      {/* Chart */}
      {!hasSnapshots && (
        <div style={{ fontSize:9, color:'#f59e0b', background:'#fffbeb', borderRadius:5, padding:'3px 6px', textAlign:'center' }}>
          ⏳ Collecting daily data
        </div>
      )}
      {chartData.length === 0 ? (
        <div style={{ textAlign:'center', color:'#94a3b8', fontSize:11, padding:'12px 0' }}>
          {hasSnapshots ? 'No data in range' : 'Select a date range'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <BarChart
            width={chartWidth}
            height={110}
            data={chartData}
            margin={{ top:16, right:4, left:0, bottom:14 }}
            barSize={BAR_W}
            barCategoryGap={BAR_GAP}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize:8, fill:'#94a3b8' }}
              interval={0} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Tooltip
              contentStyle={{ fontSize:11, borderRadius:6, border:'1px solid #e2e8f0' }}
              formatter={(v) => [v.toLocaleString(), `${ownerType} devices synced`]}
              labelFormatter={(l) => l}
            />
            <Bar dataKey="count" fill={color} radius={[3,3,0,0]}
              label={{ position:'top', fontSize:8, fill: color, formatter: v => v > 0 ? v : '' }} />
          </BarChart>
        </div>
      )}
    </div>
  );
}

export default function DeviceActivitySection({
  intuneDevices = [],
  regionFilter: parentRegion = '',
  districtFilter: parentDistrict = '',
  schoolFilter: parentSchool = '',
  dateRange = null,
  snapshotTimeline = [],
}) {
  const { t } = useTranslation();
  const BUCKET_DEF = getBucketDef(t);
  const RAG_LABEL   = getRagLabel(t);
  const [regionF,   setRegionF]   = useState(parentRegion || '');
  const [sortCol,   setSortCol]   = useState('worst');
  const [sortAsc,   setSortAsc]   = useState(true);
  const [drillDown, setDrillDown] = useState(null);  // { title, devices, preselect }

  useEffect(() => { setRegionF(parentRegion || ''); setDrillDown(null); }, [parentRegion]);
  useEffect(() => { setDrillDown(null); }, [parentDistrict, parentSchool]);

  // Derive window days from the top-bar dateRange prop
  const windowDays = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return 7;
    const diff = (new Date(dateRange.endDate) - new Date(dateRange.startDate)) / 86400000;
    return Math.max(1, Math.round(diff));
  }, [dateRange]);

  // Human-readable window label matching the top-bar selection
  const winLabel = useMemo(() => {
    if (windowDays <= 1)  return t('deviceActivity.last24h');
    if (windowDays <= 7)  return t('deviceActivity.last7days');
    if (windowDays <= 30) return t('deviceActivity.last30days');
    return t('deviceActivity.winLastDays', { count: windowDays });
  }, [windowDays, t]);

  const allDevices = useMemo(() => {
    return intuneDevices.filter(d => {
      if (regionF        && d.regionCode   !== regionF)        return false;
      if (parentDistrict && d.districtCode !== parentDistrict) return false;
      if (parentSchool   && `${d.schoolType}-${d.schoolNumber}` !== parentSchool) return false;
      return true;
    });
  }, [intuneDevices, regionF, parentDistrict, parentSchool]);

  const regions = useMemo(() => {
    const s = new Set();
    intuneDevices.forEach(d => { if (d.regionCode && d.regionCode !== 'Unknown' && d.regionCode !== 'UNK') s.add(d.regionCode); });
    // Drop region codes that don't resolve to a real Uzbekistan region name
    // (e.g. KDK, RAX, LAP — produced by malformed device names). Same pattern
    // as the top-level region dropdown in udashboard.jsx.
    return Array.from(s)
      .filter(code => {
        const name = getRegionName(code);
        return name && name !== code;
      })
      .sort((a, b) => (getRegionName(a) || a).localeCompare(getRegionName(b) || b));
  }, [intuneDevices]);

  const computeStats = useCallback((devs) => {
    const now = Date.now();
    const MS = 86400000;
    let d24h = 0, d7d = 0, d30d = 0, dOld = 0, dNever = 0;
    devs.forEach(d => {
      if (!d.lastSyncDateTime) { dNever++; return; }
      const age = (now - new Date(d.lastSyncDateTime).getTime()) / MS;
      if      (age <= 1)  d24h++;
      else if (age <= 7)  d7d++;
      else if (age <= 30) d30d++;
      else                dOld++;
    });
    const total    = devs.length;
    const inactive = dOld + dNever;
    // Also keep bucket-based connected for regional bars
    const connected = devs.filter(d => { const b = getBucket(d, windowDays); return b === 'today' || b === 'recent'; }).length;
    return { total, d24h, d7d, d30d, dOld, dNever, inactive, connected, rate: pct(connected, total) };
  }, [windowDays]);

  const teachers = useMemo(() => allDevices.filter(d => d.deviceOwnerType?.toLowerCase() === 'teacher'), [allDevices]);
  const students  = useMemo(() => allDevices.filter(d => d.deviceOwnerType?.toLowerCase() === 'student'),  [allDevices]);
  const tStats    = useMemo(() => computeStats(teachers), [teachers, computeStats]);
  const sStats    = useMemo(() => computeStats(students),  [students,  computeStats]);

  const byBuckets  = (devs, ...bs) => devs.filter(d => bs.includes(getBucket(d, windowDays)));
  const connected  = (devs)        => byBuckets(devs, 'today', 'recent');
  const allInactive = (devs)       => byBuckets(devs, 'inactive', 'never');

  const open = (title, devs, preselect) => { if (devs.length) setDrillDown({ title, devices: devs, preselect }); };

  // Per-region stats
  const regionStats = useMemo(() => {
    const map = {};
    allDevices.forEach(d => {
      const rc = d.regionCode;
      if (!rc || rc === 'Unknown' || rc === 'UNK') return;
      // Skip codes that don't resolve to a real region name. Without this,
      // malformed device names (e.g. those producing KDK) get their own row.
      const resolved = getRegionName(rc);
      if (!resolved || resolved === rc) return;
      if (!map[rc]) map[rc] = { region: d.region || resolved, T: [], S: [] };
      if      (d.deviceOwnerType?.toLowerCase() === 'teacher') map[rc].T.push(d);
      else if (d.deviceOwnerType?.toLowerCase() === 'student') map[rc].S.push(d);
    });
    return Object.entries(map).map(([rc, v]) => ({
      rc, region: v.region,
      T: v.T, tStat: computeStats(v.T),
      S: v.S, sStat: computeStats(v.S),
    })).sort((a, b) => a.region.localeCompare(b.region));
  }, [allDevices, computeStats]);

  // Per-school stats
  const schoolRows = useMemo(() => {
    const map = {};
    allDevices.forEach(d => {
      const sn = d.schoolNumber;
      if (!d.regionCode || d.regionCode === 'Unknown' || !sn || sn === 'Unknown' || sn === '000') return;
      const key = `${d.regionCode}-${d.districtCode}-${sn}`;
      if (!map[key]) map[key] = { key, school:d.school||`School ${sn}`, region:d.region||d.regionCode, district:d.district||d.districtCode, T:[], S:[] };
      if      (d.deviceOwnerType?.toLowerCase() === 'teacher') map[key].T.push(d);
      else if (d.deviceOwnerType?.toLowerCase() === 'student') map[key].S.push(d);
    });
    return Object.values(map).map(r => {
      const tStat = computeStats(r.T);
      const sStat = computeStats(r.S);
      return { ...r, tStat, sStat, worstRate: Math.min(r.T.length ? tStat.rate : 100, r.S.length ? sStat.rate : 100) };
    });
  }, [allDevices, computeStats]);

  const sortedSchools = useMemo(() => {
    return [...schoolRows].sort((a, b) => {
      const va = sortCol==='school'?a.school : sortCol==='tRate'?a.tStat.rate : sortCol==='sRate'?a.sStat.rate : a.worstRate;
      const vb = sortCol==='school'?b.school : sortCol==='tRate'?b.tStat.rate : sortCol==='sRate'?b.sStat.rate : b.worstRate;
      if (va===vb) return 0;
      if (typeof va==='string') return sortAsc?va.localeCompare(vb):vb.localeCompare(va);
      return sortAsc?va-vb:vb-va;
    });
  }, [schoolRows, sortCol, sortAsc]);

  const toggleSort = (col) => { if (sortCol===col) setSortAsc(a=>!a); else { setSortCol(col); setSortAsc(true); } };

  // ── If a drill-down is active, render that instead ──────────────────────────
  if (drillDown) {
    return <DrillDownView panel={drillDown} windowDays={windowDays} onBack={() => setDrillDown(null)} />;
  }

  // ── Shared style objects ────────────────────────────────────────────────────
  const card     = { background:'var(--color-background-primary)', border:'0.5px solid var(--color-border-tertiary)', borderRadius:12, padding:'1rem 1.25rem' };
  const divider  = '0.5px solid var(--color-border-tertiary)';
  const cardTitle = { fontSize:14, fontWeight:500, color:'var(--color-text-secondary)', marginBottom:12, paddingBottom:8, borderBottom:divider };


  // ── KPI card — old MetricCard style ─────────────────────────────────────────
  // KpiCard: shows connected + not-connected rows with counts and percentages
  const KpiCard = ({ icon, label, connectedDevs, notConnectedDevs, total, title, notTitle, invertColors = false }) => {
    const conn = connectedDevs?.length ?? 0;
    const notConn = notConnectedDevs?.length ?? 0;
    const connPct = total > 0 ? Math.round((conn / total) * 100) : 0;
    const notPct  = total > 0 ? Math.round((notConn / total) * 100) : 0;
    return (
      <div style={{
        background: 'white', borderRadius: 12, padding: '14px 16px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '0.5px solid var(--color-border-tertiary)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {icon && <span style={{ fontSize:16, opacity:0.85 }}>{icon}</span>}
          <div style={{ fontSize:11, color:'var(--color-text-secondary)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
        </div>
        {/* Top row — red when invertColors (inactive card), green otherwise */}
        <div
          onClick={() => connectedDevs?.length && open(title || label, connectedDevs)}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'8px 10px', borderRadius:8,
            background: invertColors ? '#fef2f2' : '#f0fdf4',
            cursor: connectedDevs?.length ? 'pointer' : 'default',
            transition:'background 0.15s' }}
          onMouseEnter={e => { if (connectedDevs?.length) e.currentTarget.style.background = invertColors ? '#fee2e2' : '#dcfce7'; }}
          onMouseLeave={e => { e.currentTarget.style.background = invertColors ? '#fef2f2' : '#f0fdf4'; }}
        >
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:13 }}>{invertColors ? '⚠️' : '✅'}</span>
            <span style={{ fontSize:12, color: invertColors ? '#991b1b' : '#166534', fontWeight:500 }}>
              {invertColors ? '30d+ / Never' : 'Connected'}
            </span>
          </div>
          <div style={{ textAlign:'right' }}>
            <span style={{ fontSize:20, fontWeight:700, color: invertColors ? '#991b1b' : '#166534', lineHeight:1 }}>{conn}</span>
            <span style={{ fontSize:12, color: invertColors ? '#f87171' : '#4ade80', fontWeight:600, marginLeft:6 }}>{connPct}%</span>
          </div>
        </div>
        {/* Bottom row */}
        <div
          onClick={() => notConnectedDevs?.length && open(notTitle || label + ' — not connected', notConnectedDevs)}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'8px 10px', borderRadius:8,
            background: invertColors ? '#f0fdf4' : '#fef2f2',
            cursor: notConnectedDevs?.length ? 'pointer' : 'default',
            transition:'background 0.15s' }}
          onMouseEnter={e => { if (notConnectedDevs?.length) e.currentTarget.style.background = invertColors ? '#dcfce7' : '#fee2e2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = invertColors ? '#f0fdf4' : '#fef2f2'; }}
        >
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:13 }}>{invertColors ? '✅' : '❌'}</span>
            <span style={{ fontSize:12, color: invertColors ? '#166534' : '#991b1b', fontWeight:500 }}>
              {invertColors ? 'Active ≤30d' : 'Not connected'}
            </span>
          </div>
          <div style={{ textAlign:'right' }}>
            <span style={{ fontSize:20, fontWeight:700, color: invertColors ? '#166534' : '#991b1b', lineHeight:1 }}>{notConn}</span>
            <span style={{ fontSize:12, color: invertColors ? '#4ade80' : '#f87171', fontWeight:600, marginLeft:6 }}>{notPct}%</span>
          </div>
        </div>
      </div>
    );
  };

  const TypeBadge = ({ type }) => (
    <h3 style={{ fontSize:18, marginBottom:16, color: type==='T' ? '#1976d2' : '#f57c00', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
      {type==='T' ? '👨‍🏫' : '👨‍🎓'}
      {type==='T' ? t('deviceActivity.teacherDevices') : t('deviceActivity.studentDevices')}
    </h3>
  );

  const SortTh = ({ col, label, right=true }) => (
    <th style={{ fontSize:13, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', textAlign:right?'right':'left', whiteSpace:'nowrap', cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort(col)}>
      {label}{sortCol===col?(sortAsc?' ▲':' ▼'):''}
    </th>
  );

  // clickable table cell
  const Td = ({ n, devs, title:t, preselect, color }) => (
    <td
      onClick={() => devs?.length && open(t, devs, preselect)}
      style={{ padding:'5px 8px', fontSize:13, textAlign:'right', verticalAlign:'middle', color:color||'var(--color-text-primary)', cursor:devs?.length?'pointer':'default' }}
      onMouseEnter={e=>{ if(devs?.length) e.currentTarget.style.textDecoration='underline'; }}
      onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}
    >{n}</td>
  );

  return (
    <div style={{ paddingBottom:40 }}>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:500, marginBottom:4 }}>{t("deviceActivity.pageTitle")}</h2>
        <p style={{ fontSize:14, color:'var(--color-text-secondary)' }}>
          {t("deviceActivity.pageSubtitle")}
        </p>
      </div>

      {/* Active scope indicator — shown when parent has scoped to district or school */}
      {(parentDistrict || parentSchool) && (
        <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-text-secondary)', padding:'5px 12px', background:'var(--color-background-secondary)', borderRadius:8, border:'0.5px solid var(--color-border-tertiary)', marginBottom:16 }}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2"/><path d="M4.5 6.5l1.5 1.5 2.5-2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Filtered: {parentSchool
            ? (allDevices[0]?.school || parentSchool)
            : (allDevices[0]?.district || parentDistrict)}
          {parentRegion && <span style={{ color:'var(--color-text-secondary)' }}> — {allDevices[0]?.region || parentRegion}</span>}
        </div>
      )}

      {/* ── KPI Cards ────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom:16 }}>
        <TypeBadge type="T" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 200px) minmax(0, 1fr)', gap:14, marginBottom:24 }}>
          {/* Total */}
          <div
            onClick={() => open(t("deviceActivity.allTeacherDevicesTitle"), teachers)}
            style={{
              background: 'white', borderRadius: 12, padding: '14px 16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '0.5px solid var(--color-border-tertiary)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6,
              cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)'; }}
          >
            <div style={{ fontSize:11, color:'var(--color-text-secondary)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
              📱 {t("deviceActivity.totalDevices")}
            </div>
            <div style={{ fontSize:36, fontWeight:800, color:'#0078d4', lineHeight:1 }}>{tStats.total.toLocaleString()}</div>
            <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{t('deviceActivity.clickToSeeAll')}</div>
          </div>
          {/* 24h */}
          <KpiCard icon="📅" label={t("deviceActivity.last24h")}
            total={tStats.total}
            connectedDevs={teachers.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 1)}
            notConnectedDevs={teachers.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 1)}
            title={t("deviceActivity.syncedLast24hTeachers")}
            notTitle="Teachers — not synced in 24h"
          />
          {/* 7 days */}
          <KpiCard icon="📅" label={t("deviceActivity.last7days")}
            total={tStats.total}
            connectedDevs={teachers.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 7)}
            notConnectedDevs={teachers.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 7)}
            title={t("deviceActivity.syncedLast7dTeachers")}
            notTitle="Teachers — not synced in 7 days"
          />
          {/* 30 days */}
          <KpiCard icon="📅" label={t("deviceActivity.last30days")}
            total={tStats.total}
            connectedDevs={teachers.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 30)}
            notConnectedDevs={teachers.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 30)}
            title={t("deviceActivity.syncedLast30dTeachers")}
            notTitle="Teachers — not synced in 30 days"
          />
          {/* Inactive / never — 30d+ or never synced */}
          <KpiCard icon="🔴" label={t("deviceActivity.inactiveOrNever")}
            total={tStats.total}
            connectedDevs={allInactive(teachers)}
            notConnectedDevs={teachers.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 30)}
            title={t("deviceActivity.inactiveTeachers")}
            notTitle="Teachers — active in last 30d"
            invertColors={true}
          />
          {/* Timeline chart */}
          <TimelineCard devices={teachers} label="Teachers" color="#1976d2" dateRange={dateRange} snapshotTimeline={snapshotTimeline} ownerType="Teacher" />
        </div>

        <TypeBadge type="S" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 200px) minmax(0, 1fr)', gap:14 }}>
          {/* Total */}
          <div
            onClick={() => open(t("deviceActivity.allStudentDevicesTitle"), students)}
            style={{
              background: 'white', borderRadius: 12, padding: '14px 16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '0.5px solid var(--color-border-tertiary)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6,
              cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.08)'; }}
          >
            <div style={{ fontSize:11, color:'var(--color-text-secondary)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
              📱 {t("deviceActivity.totalDevices")}
            </div>
            <div style={{ fontSize:36, fontWeight:800, color:'#f57c00', lineHeight:1 }}>{sStats.total.toLocaleString()}</div>
            <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{t('deviceActivity.clickToSeeAll')}</div>
          </div>
          {/* 24h */}
          <KpiCard icon="📅" label={t("deviceActivity.last24h")}
            total={sStats.total}
            connectedDevs={students.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 1)}
            notConnectedDevs={students.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 1)}
            title={t("deviceActivity.syncedLast24hStudents")}
            notTitle="Students — not synced in 24h"
          />
          {/* 7 days */}
          <KpiCard icon="📅" label={t("deviceActivity.last7days")}
            total={sStats.total}
            connectedDevs={students.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 7)}
            notConnectedDevs={students.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 7)}
            title={t("deviceActivity.syncedLast7dStudents")}
            notTitle="Students — not synced in 7 days"
          />
          {/* 30 days */}
          <KpiCard icon="📅" label={t("deviceActivity.last30days")}
            total={sStats.total}
            connectedDevs={students.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 30)}
            notConnectedDevs={students.filter(d => !d.lastSyncDateTime || (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 > 30)}
            title={t("deviceActivity.syncedLast30dStudents")}
            notTitle="Students — not synced in 30 days"
          />
          {/* Inactive / never — 30d+ or never synced */}
          <KpiCard icon="🔴" label={t("deviceActivity.inactiveOrNever")}
            total={sStats.total}
            connectedDevs={allInactive(students)}
            notConnectedDevs={students.filter(d => d.lastSyncDateTime && (Date.now()-new Date(d.lastSyncDateTime).getTime())/86400000 <= 30)}
            title={t("deviceActivity.inactiveStudents")}
            notTitle="Students — active in last 30d"
            invertColors={true}
          />
          {/* Timeline chart */}
          <TimelineCard devices={students} label="Students" color="#f57c00" dateRange={dateRange} snapshotTimeline={snapshotTimeline} ownerType="Student" />
        </div>
      </div>

      {/* ── Regional bars ─────────────────────────────────────────────────────── */}
      {!regionF && !parentDistrict && !parentSchool && regionStats.length > 1 && (
        <div style={{ ...card, marginBottom:16, padding:'1rem 1.25rem' }}>
          <div style={cardTitle}>{t('deviceActivity.byRegionTitle')} ({winLabel})</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            {[{label:'teachers',key:'T',statKey:'tStat'},{label:'students',key:'S',statKey:'sStat'}].map(({label,key,statKey}) => (
              <div key={label}>
                <div style={{ fontSize:15, color:'var(--color-text-primary)', fontWeight:700, marginBottom:10, letterSpacing:'.01em' }}>{label === "teachers" ? t("deviceActivity.teachers") : t("deviceActivity.students")}</div>
                {regionStats.map(r => {
                  const st   = r[statKey];
                  const devs = r[key];
                  return (
                    <div key={r.rc} style={{ display:'grid', gridTemplateColumns:'110px 1fr 42px', alignItems:'center', gap:8, marginBottom:6 }}>
                      <div
                        style={{ fontSize:13, color:'var(--color-text-secondary)', cursor:st.total?'pointer':'default' }}
                        onClick={() => st.total && open(`${r.region} — ${label}`, devs)}
                      >{r.region}</div>
                      <div style={{ height:7, borderRadius:4, background:'var(--color-background-secondary)', overflow:'hidden' }}>
                        <div style={{ height:'100%', borderRadius:4, width:`${st.rate}%`, background:BAR_COLOR(st.rate), transition:'width .4s' }} />
                      </div>
                      <div style={{ fontSize:13, fontWeight:500, textAlign:'right', color:NUM_COLOR(st.rate) }}>{st.rate}%</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── School league table ───────────────────────────────────────────────── */}
      <div style={{ ...card, padding:0, overflow:'hidden' }}>
        <div style={{ padding:'1rem 1.25rem 0.75rem', borderBottom:divider }}>
          <div style={cardTitle}>{t("deviceActivity.schoolBreakdown")}</div>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:600 }}>
            <thead>
              <tr style={{ borderBottom:divider }}>
                <SortTh col="school" label={t("deviceActivity.schoolCol")} right={false} />
                <th style={{ fontSize:13, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', width:28 }}></th>
                <th style={{ fontSize:13, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', textAlign:'right' }}>total</th>
                <th style={{ fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', textAlign:'right' }}>24h</th>
                <SortTh col="tRate" label={t("deviceActivity.rateCol")} />
                <th style={{ fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', textAlign:'right' }}>7–30d</th>
                <th style={{ fontSize:11, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', textAlign:'right' }}>30d+</th>
                <th style={{ fontSize:13, fontWeight:500, color:'var(--color-text-secondary)', padding:'0 8px 8px', width:64 }}></th>
                <SortTh col="worst" label={t("deviceActivity.statusCol")} />
              </tr>
            </thead>
            <tbody>
              {sortedSchools.map(row => (
                <React.Fragment key={row.key}>
                  <tr
                    style={{ cursor:'pointer' }}
                    onClick={() => open(`${row.school} — ${row.region} / ${row.district}`, [...row.T,...row.S])}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--color-background-secondary)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                  >
                    <td colSpan={9} style={{ fontSize:14, fontWeight:500, padding:'12px 8px 4px', borderTop:divider, color:'var(--color-text-primary)' }}>
                      {row.school}
                      <span style={{ fontSize:12, fontWeight:400, color:'var(--color-text-secondary)', marginLeft:8 }}>{row.region} / {row.district}</span>
                    </td>
                  </tr>
                  {row.T.length > 0 && (
                    <tr>
                      <td style={{ padding:'4px 8px' }}></td>
                      <td style={{ padding:'4px 4px' }}><span style={{ background:'#E6F1FB', color:'#185FA5', padding:'1px 6px', borderRadius:5, fontSize:11, fontWeight:600 }}>T</span></td>
                      <Td n={row.tStat.total}  devs={row.T} title={`${row.school} — all teachers`} />
                      <Td n={byBuckets(row.T,'today').length} devs={byBuckets(row.T,'today')} title={`${row.school} — teachers synced today`} color="#3B6D11" preselect={['today']} />
                      <Td n={`${row.tStat.rate}%`} devs={connected(row.T)} title={`${row.school} — teachers connected`} color={NUM_COLOR(row.tStat.rate)} preselect={['today','recent']} />
                      <Td n={byBuckets(row.T,'overdue').length||'—'} devs={byBuckets(row.T,'overdue')} title={`${row.school} — teachers 7–30d`} color={byBuckets(row.T,'overdue').length?'#854F0B':'var(--color-text-secondary)'} preselect={['overdue']} />
                      <Td n={allInactive(row.T).length||'—'} devs={allInactive(row.T)} title={`${row.school} — teachers 30d+`} color={allInactive(row.T).length?'#A32D2D':'var(--color-text-secondary)'} preselect={['inactive','never']} />
                      <td style={{ padding:'4px 8px' }}><div style={{ height:5, borderRadius:3, background:'var(--color-background-secondary)', overflow:'hidden' }}><div style={{ height:'100%', borderRadius:3, width:`${row.tStat.rate}%`, background:BAR_COLOR(row.tStat.rate) }}/></div></td>
                      <td style={{ padding:'4px 8px' }}><span style={{ ...RAG_STYLES[ragStatus(row.tStat.rate)], padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:500 }}>{RAG_LABEL[ragStatus(row.tStat.rate)]}</span></td>
                    </tr>
                  )}
                  {row.S.length > 0 && (
                    <tr>
                      <td style={{ padding:'4px 8px 10px' }}></td>
                      <td style={{ padding:'4px 4px 10px' }}><span style={{ background:'#EAF3DE', color:'#3B6D11', padding:'1px 6px', borderRadius:5, fontSize:11, fontWeight:600 }}>S</span></td>
                      <Td n={row.sStat.total}  devs={row.S} title={`${row.school} — all students`} />
                      <Td n={byBuckets(row.S,'today').length} devs={byBuckets(row.S,'today')} title={`${row.school} — students synced today`} color="#3B6D11" preselect={['today']} />
                      <Td n={`${row.sStat.rate}%`} devs={connected(row.S)} title={`${row.school} — students connected`} color={NUM_COLOR(row.sStat.rate)} preselect={['today','recent']} />
                      <Td n={byBuckets(row.S,'overdue').length||'—'} devs={byBuckets(row.S,'overdue')} title={`${row.school} — students 7–30d`} color={byBuckets(row.S,'overdue').length?'#854F0B':'var(--color-text-secondary)'} preselect={['overdue']} />
                      <Td n={allInactive(row.S).length||'—'} devs={allInactive(row.S)} title={`${row.school} — students 30d+`} color={allInactive(row.S).length?'#A32D2D':'var(--color-text-secondary)'} preselect={['inactive','never']} />
                      <td style={{ padding:'4px 8px 10px' }}><div style={{ height:5, borderRadius:3, background:'var(--color-background-secondary)', overflow:'hidden' }}><div style={{ height:'100%', borderRadius:3, width:`${row.sStat.rate}%`, background:BAR_COLOR(row.sStat.rate) }}/></div></td>
                      <td style={{ padding:'4px 8px 10px' }}><span style={{ ...RAG_STYLES[ragStatus(row.sStat.rate)], padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:500 }}>{RAG_LABEL[ragStatus(row.sStat.rate)]}</span></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
